import "../sellscreendialogs.scss";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type ThunkDispatch } from "redux-thunk";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { addPaymentToSale, type SaleAddPaymentOptions } from "../../../../Actions/SellScreen/SellScreenPaymentActions";
import Button from "../../../../Common/Buttons/Button";
import CurrencyInput from "../../../../Common/Forms/CurrencyInput";
import RetractableInput from "../../../../Common/Forms/RetractableInput";
import NoTranslate from "../../../../Common/NoTranslate";
import PopupManager from "../../../../Common/Popups/PopupManager";
import { withDataSource } from "../../../../Data/DataStores/WithDataSource";
import { type RootState } from "../../../../Reducers/ShopfrontReducers";
import { currency } from "../../../../Utilities/Generic/Numbers";

const connector = connect((state: RootState) => {
    let usedLoyalty = 0;

    for(let i = 0, l = state.sellscreen.sale.payments.length; i < l; i++) {
        if(state.sellscreen.sale.payments[i].status !== "completed") {
            continue;
        }

        if(state.sellscreen.sale.payments[i].type !== "loyalty") {
            continue;
        }

        try {
            const metaData = JSON.parse(state.sellscreen.sale.payments[i].metadata);

            usedLoyalty += metaData.loyalty_points;
        } catch {
            usedLoyalty += state.sellscreen.sale.payments[i].amount;
        }
    }

    const loyaltyDetails = [];
    let redeemablePoints = 0;
    let redeemableDollar = 0;

    for(let i = 0, l = state.sellscreen.sale.products.length; i < l; i++) {
        const points = Math.round(state.sellscreen.sale.products[i].loyalty.redeem * 100) / 100;
        const dollar = (
            state.sellscreen.sale.products[i].prices ?
                (
                    state.sellscreen.sale.products[i].prices.price +
                    state.sellscreen.sale.products[i].prices.additionalPrice[1]
                ) :
                0
        );
        const rate = dollar === 0 ? 0 : points / dollar;

        loyaltyDetails.push({
            rate  : rate,
            dollar: dollar,
            points: points,
        });

        if(points !== 0) {
            redeemablePoints += points;
            redeemableDollar += points === 0 ? 0 : dollar;
        }
    }

    // In ascending order of redemption rate
    loyaltyDetails.sort((a, b) => {
        return a.rate - b.rate;
    });

    return {
        remaining       : state.sellscreen.sale.totals.remaining,
        customer        : state.sellscreen.sale.customer as any,
        usedLoyalty,
        products        : state.sellscreen.sale.products,
        redeemablePoints: Math.round(redeemablePoints * 100) / 100,
        redeemableDollar: Math.round(redeemableDollar * 100) / 100,
        loyaltyDetails,
    };
});

type PropsFromRedux = ConnectedProps<typeof connector> & {
    dispatch: ThunkDispatch<any, any, any>;
    data: Promise<any>;
};

interface Props extends PropsFromRedux {
    method: string;
    amount: number;
    options?: SaleAddPaymentOptions;
    usedLoyalty: number;
}

interface CalculatedType {
    dollar: number;
    loyalty: number;
}

interface State {
    dollar: string;
    loyalty: string;
    calculated: CalculatedType;
    customer: boolean | any;
}

export class RequestLoyalty extends React.Component<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props);

        this.addLoyalty = this.addLoyalty.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.cancelRequest = this.cancelRequest.bind(this);
        this.loadCustomer = this.loadCustomer.bind(this);

        const amount = props.amount > 0 ? props.amount : props.redeemableDollar;
        const loyalty = this.calculateLoyalty(amount);

        this.state = {
            dollar    : amount.toString(),
            loyalty   : loyalty.toString(),
            calculated: {
                dollar: amount,
                loyalty,
            },
            customer: false,
        };
    }

    public componentDidMount(): void {
        if(this.props.customer) {
            this.loadCustomer();
        } else {
            // NOTE: This should never happen
            PopupManager.warning("No customer in sale, Loyalty points cannot be selected");
            this.props.dispatch(hideDialog());
        }
    }

    public loadCustomer(): Promise<void> {
        return this.props.data
            .then(source => {
                return source.customers.get(this.props.customer.uuid) as any;
            })
            .then(customer => {
                this.setState({
                    customer: customer,
                }, this.checkBounds);
            });
    }

    public checkBounds = (): void => {
        let dollar = this.state.calculated.dollar;
        let loyalty = this.state.calculated.loyalty;

        let aboveMax = false;

        const customer = this.state.customer as any;

        if(typeof customer !== "boolean") {
            let maxLoyalty = Math.round((parseFloat(customer.loyalty_points) - this.props.usedLoyalty) * 100) / 100;

            if(maxLoyalty > this.props.redeemablePoints) {
                maxLoyalty = this.props.redeemablePoints;
            }

            if(loyalty > maxLoyalty) {
                loyalty = maxLoyalty;
                dollar = this.calculateDollar(loyalty);
                aboveMax = true;
            }
        }

        if(dollar > this.props.redeemableDollar) {
            dollar = this.props.redeemableDollar;
            loyalty = this.calculateLoyalty(dollar);
            aboveMax = true;
        }

        if(aboveMax) {
            this.setState({
                calculated: {
                    dollar,
                    loyalty,
                },
                loyalty: loyalty.toString(),
                dollar : dollar.toString(),
            });
        }
    };

    public handleDollarChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const value = e.currentTarget.value;

        this.setState(prevState => {
            let parsed = parseFloat(value);

            let loyalty = prevState.loyalty;
            let loyaltyCalculated = 0;

            if(!isNaN(parsed)) {
                loyaltyCalculated = this.calculateLoyalty(parsed);
                loyalty = loyaltyCalculated.toString();
            } else {
                parsed = 0;
            }

            this.setState({
                loyalty   : loyalty,
                dollar    : value,
                calculated: {
                    dollar : parsed,
                    loyalty: loyaltyCalculated,
                },
            }, this.checkBounds);
        });
    };

    public handleLoyaltyChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const value = e.currentTarget.value;

        this.setState(prevState => {
            let parsed = parseFloat(value);

            let dollar = prevState.dollar;
            let dollarCalculated = 0;

            if(!isNaN(parsed)) {
                dollarCalculated = this.calculateDollar(parsed);
                dollar = dollarCalculated.toString();
            } else {
                parsed = 0;
            }

            this.setState({
                loyalty   : value,
                dollar    : dollar,
                calculated: {
                    dollar : dollarCalculated,
                    loyalty: parsed,
                },
            }, this.checkBounds);
        });
    };

    public handleKeyDown(
        e: React.KeyboardEvent<HTMLInputElement>
    ): void {
        if(e.key === "Enter") {
            this.addLoyalty(e);
        }
    }

    public calculateLoyalty(amount: number): number {
        let remain = amount;
        let points = 0;

        for(let i = 0, l = this.props.loyaltyDetails.length; i < l; i++) {
            if(this.props.loyaltyDetails[i].rate !== 0) {
                let proportion = 1;

                if(remain < this.props.loyaltyDetails[i].dollar) {
                    proportion = remain / this.props.loyaltyDetails[i].dollar;
                    remain = 0;
                } else {
                    remain -= this.props.loyaltyDetails[i].dollar;
                }

                points += Math.round(this.props.loyaltyDetails[i].points * proportion * 100) / 100;

                if(remain <= 0) {
                    break;
                }
            }
        }

        return points;
    }

    public calculateDollar(amount: number): number {
        let remain = amount;
        let dollar = 0;

        for(let i = 0, l = this.props.loyaltyDetails.length; i < l; i++) {
            if(this.props.loyaltyDetails[i].rate !== 0) {
                let proportion = 1;

                if(remain < this.props.loyaltyDetails[i].points) {
                    proportion = remain / this.props.loyaltyDetails[i].points;
                    remain = 0;
                } else {
                    remain -= this.props.loyaltyDetails[i].points;
                }

                dollar += Math.round(this.props.loyaltyDetails[i].dollar * proportion * 100) / 100;

                if(remain <= 0) {
                    break;
                }
            }
        }

        return dollar;
    }

    public addLoyalty(
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>
    ): void {
        if(!this.state.dollar || !this.state.calculated.dollar) {
            PopupManager.warning("Please enter a valid amount");
            e.stopPropagation();

            return;
        }

        this.props.dispatch(hideDialog());
        this.props.dispatch(addPaymentToSale(this.props.method, this.state.calculated.dollar, {
            ...this.props.options,
            exact  : false,
            loyalty: this.state.calculated.loyalty,
        }));
    }

    public cancelRequest(): void {
        this.props.dispatch(hideDialog());
    }

    public render(): React.ReactNode {
        let loyalty = 0;

        const customer: any = this.state.customer;

        if(customer) {
            loyalty = Math.round((parseFloat(customer.loyalty_points) - this.props.usedLoyalty) * 100) / 100;
        }

        return (
            <div className="request-loyalty">
                <p>Please enter dollars or loyalty points to redeem</p>
                <div className="text-group">
                    <span>
                        <div>
                            Sale Total
                        </div>
                        <div><NoTranslate>{currency(this.props.remaining)}</NoTranslate></div>
                    </span>
                    <span>
                        <div>
                            Customer Points
                        </div>
                        <div>{loyalty}</div>
                    </span>
                </div>
                <div className="loyalty-select">
                    <RetractableInput
                        label="Dollar Loyalty"
                        hasValue={!!this.state.dollar}
                        left="1.75rem"
                    >
                        {(onFocus, onBlur) => (
                            <CurrencyInput
                                type="number"
                                step="any"
                                value={this.state.dollar}
                                onChange={this.handleDollarChange}
                                onKeyDown={this.handleKeyDown}
                                onFocus={onFocus}
                                onBlur={onBlur}
                                autoFocus
                            />
                        )}
                    </RetractableInput>
                    <RetractableInput
                        label="Loyalty Points"
                        hasValue={!!this.state.loyalty}
                    >
                        {(onFocus, onBlur) => (
                            <input
                                type="number"
                                step="any"
                                value={this.state.loyalty}
                                onChange={this.handleLoyaltyChange}
                                onKeyDown={this.handleKeyDown}
                                onFocus={onFocus}
                                onBlur={onBlur}
                            />
                        )}
                    </RetractableInput>
                </div>
                <div className="text-group">
                    <span>
                        <div>
                            Sale Remaining
                        </div>
                        <div>
                            <NoTranslate>
                                {currency(this.props.remaining - this.state.calculated.dollar)}
                            </NoTranslate>
                        </div>
                    </span>
                    <span>
                        <div>
                            Points Remaining
                        </div>
                        <div>{Math.round((loyalty - this.state.calculated.loyalty) * 100) / 100}</div>
                    </span>
                </div>
                <div className="buttons">
                    <Button type="button" classes={[ "button-cancel" ]} onClick={this.cancelRequest}>Cancel</Button>
                    <Button type="button" classes={[ "button-save" ]} onClick={this.addLoyalty}>Add</Button>
                </div>
            </div>
        );
    }
}

export default connector(withDataSource(RequestLoyalty));
